import React, { useRef, useState, useEffect } from "react";
import SignaturePad from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Eraser, CheckCircle, Signature, UploadSimple, MagicWand } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SignatureCapture({ onChange, testId = "signature-pad", allowSaved = true, autoUseSaved = true }) {
  const ref = useRef();
  const fileRef = useRef();
  const { user } = useAuth();
  const [usingSaved, setUsingSaved] = useState(false);
  const [uploadedPreview, setUploadedPreview] = useState("");
  const [cleaning, setCleaning] = useState(false);

  // Belge imzalama akışlarında (bordro, izin vb.) kayıtlı imza otomatik kullanılır;
  // personelden tekrar imza istenmez.
  useEffect(() => {
    if (autoUseSaved && user?.signature) {
      setUsingSaved(true);
      onChange?.(user.signature);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoUseSaved, user?.signature]);

  const autoLocked = autoUseSaved && !!user?.signature;

  const clear = () => {
    ref.current?.clear();
    setUsingSaved(false);
    setUploadedPreview("");
    onChange?.("");
  };

  const save = () => {
    if (!ref.current) return;
    try {
      if (typeof ref.current.isEmpty === "function" && ref.current.isEmpty()) return;
      let data = "";
      if (typeof ref.current.toDataURL === "function") {
        data = ref.current.toDataURL("image/png");
      } else if (typeof ref.current.getCanvas === "function") {
        data = ref.current.getCanvas().toDataURL("image/png");
      }
      if (data) {
        setUsingSaved(false);
        setUploadedPreview("");
        onChange?.(data);
      }
    } catch (err) {
      console.error("SignatureCapture save error:", err);
    }
  };

  const useSaved = () => {
    if (user?.signature) {
      setUsingSaved(true);
      setUploadedPreview("");
      onChange?.(user.signature);
    }
  };

  const onUploadFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(f.type)) {
      toast.error("Sadece PNG, JPG veya WEBP imza görseli yükleyebilirsiniz");
      e.target.value = "";
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Görsel 2 MB'den küçük olmalıdır");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result || "");
      // Otomatik arka plan temizle — imza dosyası genelde beyaz kağıt zemin
      // üzerinde geliyor; kullanıcı istemedikçe "sadece imza" olsun.
      setUploadedPreview(dataUrl);
      setUsingSaved(false);
      onChange?.(dataUrl);
      setCleaning(true);
      try {
        const { data } = await api.post("/signatures/clean-background", { signature: dataUrl });
        if (data?.signature) {
          setUploadedPreview(data.signature);
          onChange?.(data.signature);
          toast.success("Arka plan temizlendi — sadece imzanız korundu");
        }
      } catch (err) {
        // Hata durumunda orijinal yükleme korunur — kullanıcıyı bloklamayalım
        console.warn("bg clean failed", err);
      } finally { setCleaning(false); }
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const manualCleanBackground = async () => {
    const current = uploadedPreview || (usingSaved && user?.signature) || "";
    if (!current) { toast.error("Önce bir imza yükleyin veya kayıtlı imzayı kullanın"); return; }
    setCleaning(true);
    try {
      const { data } = await api.post("/signatures/clean-background", { signature: current });
      if (data?.signature) {
        setUploadedPreview(data.signature);
        setUsingSaved(false);
        onChange?.(data.signature);
        toast.success("Arka plan yeniden temizlendi");
      }
    } catch (e) { toast.error("İşlem başarısız"); }
    finally { setCleaning(false); }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {autoLocked ? (
        <>
          <div className="rounded-md border border-dashed border-border p-4 flex items-center justify-center bg-[repeating-conic-gradient(#f4f4f5_0deg_25%,#ffffff_0deg_50%)] bg-[length:14px_14px]">
            <img src={user.signature} alt="kayıtlı imza" className="h-32 max-w-full object-contain" data-testid={`${testId}-auto`} />
          </div>
          <div className="text-[11px] text-muted-foreground text-center">
            Kayıtlı imzanız otomatik kullanılacaktır.
          </div>
        </>
      ) : (
        <>
      {usingSaved && user?.signature ? (
        <div className="rounded-md border border-dashed border-border p-4 flex items-center justify-center bg-[repeating-conic-gradient(#f4f4f5_0deg_25%,#ffffff_0deg_50%)] bg-[length:14px_14px]">
          <img src={user.signature} alt="kayıtlı imza" className="h-32 max-w-full object-contain" />
        </div>
      ) : uploadedPreview ? (
        <div className="rounded-md border border-dashed border-border p-4 flex items-center justify-center bg-[repeating-conic-gradient(#f4f4f5_0deg_25%,#ffffff_0deg_50%)] bg-[length:14px_14px]">
          <img src={uploadedPreview} alt="yüklenen imza" className="h-32 max-w-full object-contain" />
        </div>
      ) : (
        <div className="rounded-md bg-white border border-border">
          <SignaturePad
            ref={ref}
            canvasProps={{ className: "sig-canvas" }}
            onEnd={save}
          />
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onUploadFile} data-testid={`${testId}-file`} />
      <div className="flex flex-wrap gap-2 justify-end">
        {allowSaved && user?.signature && !usingSaved && (
          <Button type="button" size="sm" variant="secondary" onClick={useSaved} data-testid={`${testId}-use-saved`}>
            <Signature size={16} className="mr-1" /> Kayıtlı imzayı kullan
          </Button>
        )}
        <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid={`${testId}-upload`}>
          <UploadSimple size={16} className="mr-1" /> {cleaning ? "İşleniyor…" : "Görsel Yükle"}
        </Button>
        {(uploadedPreview || (usingSaved && user?.signature)) && (
          <Button type="button" size="sm" variant="outline" onClick={manualCleanBackground} disabled={cleaning} data-testid={`${testId}-clean-bg`} title="İmzayı korurken arka plan pikselleri şeffaflaştırılır">
            <MagicWand size={16} className="mr-1" /> Arka Planı Temizle
          </Button>
        )}
        <Button type="button" size="sm" variant="outline" onClick={clear} data-testid={`${testId}-clear`}>
          <Eraser size={16} className="mr-1" /> Temizle
        </Button>
        {!usingSaved && !uploadedPreview && (
          <Button type="button" size="sm" onClick={save} data-testid={`${testId}-save`}>
            <CheckCircle size={16} className="mr-1" /> Onayla
          </Button>
        )}
      </div>
        </>
      )}
    </div>
  );
}
